'use client';

import {
  Typography,
  TypographyMods,
  TypographySizes,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';
import { useMemo } from 'react';

import { Breadcrumbs } from '~components/BreadCrumbs/Breadcrumbs';
import {
  BreadCrumbsLink,
  BreadCrumbsMod,
} from '~components/BreadCrumbs/Breadcrumbs.types';
import { NextImage } from '~components/NextImage/NextImage';
import { NextImageMods } from '~components/NextImage/NextImage.types';
import { Section } from '~components/Section/Section';
import { getLocaleDate } from '~helpers/date/getDate';
import { useCategory } from '~hooks/useCategory';

import styles from './ArticlePageHero.module.scss';
import { ArticlePageHeroProps } from './ArticlePageHero.types';

export function ArticlePageHero({
  title,
  text,
  author,
  image,
  imageWithBg,
  publishedAt,
  updatedAt,
  categories,
  ...sectionProps
}: ArticlePageHeroProps) {
  const { category } = useCategory();
  const currentCategory = category ? category : categories?.[0];

  const breadCrumbsLinks = useMemo(() => {
    const links: BreadCrumbsLink[] = [
      { id: 1, title: 'Главная', href: '/blog' },
    ];

    if (currentCategory) {
      links.push({
        id: 2,
        title: currentCategory.title,
        href: currentCategory.href,
      });
    }

    return links;
  }, [currentCategory]);

  return (
    <Section
      className={classNames(styles.section, imageWithBg && styles.imageWithBg)}
      {...sectionProps}
    >
      <div className={styles.content}>
        <Breadcrumbs
          links={breadCrumbsLinks}
          mod={imageWithBg ? BreadCrumbsMod.White : BreadCrumbsMod.Gray}
        />
        <div className={styles.contentText}>
          <Typography
            tagName={TypographySizes.h1}
            mods={[TypographyMods.titleXl]}
            className={styles.title}
          >
            {title}
          </Typography>
          <Typography mods={[TypographyMods.bodyM]} className={styles.text}>
            {text}
          </Typography>
          {updatedAt && (
            <Typography
              tagName={TypographySizes.div}
              mods={[TypographyMods.bodyS]}
              className={styles.updateDate}
            >
              Обновлено {getLocaleDate(new Date(updatedAt))}
            </Typography>
          )}
          {publishedAt && (
            <Typography
              tagName={TypographySizes.div}
              mods={[TypographyMods.bodyS]}
              className={styles.date}
            >
              {getLocaleDate(new Date(publishedAt))}
            </Typography>
          )}
        </div>
        {author && (
          <div className={styles.author}>
            {author?.image && (
              <NextImage className={styles.authorImage} {...author.image} />
            )}
            <div className={styles.authorContent}>
              <Typography
                tagName={TypographySizes.div}
                mods={[TypographyMods.bodyS]}
                className={styles.authorName}
              >
                {author?.name}
              </Typography>
              <Typography
                tagName={TypographySizes.div}
                mods={[TypographyMods.captionM]}
                className={styles.authorDesc}
              >
                Автор статьи
              </Typography>
            </div>
          </div>
        )}
      </div>
      {image && (
        <div className={styles.media}>
          <NextImage
            className={styles.image}
            classNameInner={styles.imageInner}
            mods={!imageWithBg ? [NextImageMods.FitContain] : []}
            {...image}
          />
        </div>
      )}
    </Section>
  );
}
